import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField, NumberField, SelectField } from '@/components/forms';
import { onlyEnabled } from '@/config/preview-features';
import type { PreviewFeature } from '@/types';

import { toDefaults, toFilters } from '../filters';
import { deviceFiltersSchema } from '../schemas';
import type { DeviceFilterFormValues, DeviceListFilters } from '../types';

/** CropX is outside the release scope (ADR-0034, ADO 121238); `onlyEnabled` drops it at render. */
const DEVICE_TYPE_OPTIONS: { value: string; label: string; feature?: PreviewFeature }[] = [
  { value: 'dm_device', label: 'DM Device' },
  { value: 'cropx', label: 'CropX', feature: 'cropx' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export type DeviceFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: DeviceListFilters;
  categoryOptions: { value: string; label: string }[];
  componentOptions: { value: string; label: string }[];
  onApply: (filters: DeviceListFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<DeviceFilterDrawerProps, 'open'>;

function FilterBody({ value, categoryOptions, componentOptions, onApply, onClose }: BodyProps) {
  const form = useForm<DeviceFilterFormValues>({
    resolver: zodResolver(deviceFiltersSchema),
    defaultValues: toDefaults(value),
    mode: 'onBlur',
  });

  const handleApply = form.handleSubmit((values) => {
    onApply(toFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <SelectField
        name="deviceType"
        control={form.control}
        label="Device Type"
        placeholder="Select"
        allowClear
        options={onlyEnabled(DEVICE_TYPE_OPTIONS)}
      />

      <SelectField
        name="status"
        control={form.control}
        label="Device Status"
        placeholder="Select"
        allowClear
        options={STATUS_OPTIONS}
      />

      <MultiSelectField
        name="categoryIds"
        control={form.control}
        label="Category"
        placeholder="Select"
        options={categoryOptions}
      />

      <MultiSelectField
        name="componentIds"
        control={form.control}
        label="Applicable Components"
        placeholder="Select"
        options={componentOptions}
      />

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Price Range ($)</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="priceFrom"
            control={form.control}
            aria-label="Price From"
            placeholder="From"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="priceTo"
            control={form.control}
            aria-label="Price To"
            placeholder="To"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Installation Fee ($)</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="installationFeeFrom"
            control={form.control}
            aria-label="Installation Fee From"
            placeholder="From"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="installationFeeTo"
            control={form.control}
            aria-label="Installation Fee To"
            placeholder="To"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Subscription Price ($)</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="subscriptionPriceFrom"
            control={form.control}
            aria-label="Subscription Price From"
            placeholder="From"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
          <NumberField
            name="subscriptionPriceTo"
            control={form.control}
            aria-label="Subscription Price To"
            placeholder="To"
            min={0}
            precision={2}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Available Devices</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="availableFrom"
            control={form.control}
            aria-label="Available From"
            placeholder="From"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="availableTo"
            control={form.control}
            aria-label="Available To"
            placeholder="To"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend>Warranty Period (Months)</legend>
        <div style={{ display: 'flex', gap: 12 }}>
          <NumberField
            name="warrantyFrom"
            control={form.control}
            aria-label="Warranty From"
            placeholder="From"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
          <NumberField
            name="warrantyTo"
            control={form.control}
            aria-label="Warranty To"
            placeholder="To"
            min={0}
            precision={0}
            style={{ width: '100%' }}
          />
        </div>
      </fieldset>
    </Drawer>
  );
}

export function DeviceFilterDrawer({ open, ...rest }: DeviceFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
